import { useState } from 'react';
import { Box, H4, Link } from '@bigcommerce/big-design';
import { AddCircleOutlineIcon, CheckIcon } from '@bigcommerce/big-design-icons';
import { createMarkup } from '../../../../../../utils';
import AdditionalInfoPopup from '../AdditionalInfoPopup';

const productOptionsToIgnore = ['color', 'price code'];

// does this product have variations other than productOptionsToIgnore
function productOptionsCheck(product) {
	return product.productOptions.some(
		(option) => !productOptionsToIgnore.includes(option),
	);
}

function allConfigurableOptionsAreSet(configurableOptions, optionValues) {
	if (configurableOptions.length === 0) return true;

	return configurableOptions.every(
		(configurableOption) => optionValues[configurableOption.display_name],
	);
	// verify all the configurable options are set
}

function allQuestionsAreSet(questions, optionValues) {
	if (questions.length === 0) return true;

	return questions.every(
		({ question }) => optionValues[question] !== undefined,
	); // verify all questions are set
}

function allOptionsAreSet(product) {
	// find the configurable options that aren't color or price code
	const configurableProductOptions = product.productOptions.filter(
		(option) =>
			!productOptionsToIgnore.includes(option.display_name.toLowerCase()),
	);
	// evaluates to a boolean value
	const allConfigurableOptionsCheck = allConfigurableOptionsAreSet(
		configurableProductOptions,
		product.option_values,
	);
	// evaluates to a boolean value
	const allQuestionAreSetCheck = allQuestionsAreSet(
		product.questions,
		product.option_values,
	);

	return allQuestionAreSetCheck && allConfigurableOptionsCheck;
}

function AdditionalDetailsInfo(props) {
	return (
		<div className="optionText">
			{props.icon}
			{props.prefixText}
			<Link
				className="optionLink"
				paddingTop="xxxSmall"
				onClick={props.onClick}
			>
				{props.linkText}
			</Link>
		</div>
	);
}

function AnsweredQuestions({ answerSet }) {
	return (
		answerSet.length > 0 && (
			<>
				<H4 margin="none">Questions:</H4>
				{answerSet.map(([question, answer]) => (
					<p
						key={question}
						style={{
							margin: '0',
						}}
					>
						{question}: {answer}
					</p>
				))}
			</>
		)
	);
}

/**
 * @formatVariationOptionsForQuestions Format the variation Options to be added to the grid correctly with questions if present
 * @formattedQuestions passed in from AdditionalInfoPopup.js to copy the already formatted questions with the variation options
 * @returns {all the questions (if present) and all the variation options formatted to show as intended under the description column}
 */
export function formatVariationOptionsForQuestions(
	product,
	handler,
	formattedQuestions,
) {
	const productVariationsWithoutColorOrPriceCode =
		product.productOptions.reduce(
			(optionsArray, option) => [
				...optionsArray,
				...(!productOptionsToIgnore.includes(
					option?.display_name.toLowerCase(),
				)
					? [
							{
								question: option.display_name,
								answers: option.option_values.map(
									(value) => value.label,
								),
								selectedAnswer: '',
								handler,
							},
					  ]
					: []),
			],
			[],
		);

	return [
		...(formattedQuestions || product.questions),
		...productVariationsWithoutColorOrPriceCode,
	];
}

function OptionsText({ product, openModel }) {
	let productHasOptions = false;
	let questionsWithVariantOptions = [];

	if (!product.is_custom_product) {
		productHasOptions =
			product.questions.length || productOptionsCheck(product);
		questionsWithVariantOptions =
			formatVariationOptionsForQuestions(product);
	}

	switch (true) {
		case product.is_custom_product:
			return <AdditionalDetailsInfo prefixText="" />;
		case productHasOptions && allOptionsAreSet(product): {
			const answerSet = questionsWithVariantOptions.map(
				({ question }) => {
					const response = product.option_values[question];
					return [question, response];
				},
			);

			return (
				<>
					<AdditionalDetailsInfo
						icon={
							<CheckIcon
								className="statusIcon"
								color="success"
								size="Small"
							/>
						}
						prefixText={
							<span className="infoText">
								Additional details added.
							</span>
						}
						onClick={openModel}
						linkText={<span>Edit</span>}
					/>
					<AnsweredQuestions answerSet={answerSet} />
				</>
			);
		}
		case productHasOptions && !allOptionsAreSet(product):
			return (
				<AdditionalDetailsInfo
					icon={<span className="statusIcon">&#9888;&#65039;</span>}
					prefixText={
						<span className="errorText">
							Additional details required.
						</span>
					}
					onClick={openModel}
					linkText={<span>View Details</span>}
				/>
			);
		default:
			return (
				<AdditionalDetailsInfo
					icon={
						<AddCircleOutlineIcon
							className="statusIcon"
							size="medium"
							color="secondary70"
						/>
					}
					prefixText=""
					onClick={openModel}
					linkText={<span>Add a comment</span>}
				/>
			);
	}
}

export function DescriptionOptionsColumn(product) {
	const lengthToCollapseAfter = 400;
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [collapsed, setCollapsed] = useState(true);
	const text = collapsed
		? product.description?.slice(0, lengthToCollapseAfter)
		: product.description;
	const description = createMarkup(text);
	// When the additional product is added - Do not display the description
	if (product.isAddOn) return null;
	return (
		<div style={{ minWidth: '250px' }}>
			<div dangerouslySetInnerHTML={description} />
			{product.description.length > lengthToCollapseAfter && (
				<Box marginTop="small" marginBottom="medium">
					<Link onClick={() => setCollapsed(!collapsed)}>
						{collapsed ? 'Show More' : 'Show Less'}
					</Link>
				</Box>
			)}
			<OptionsText
				product={product}
				openModel={() => setModalIsOpen(true)}
			/>
			{modalIsOpen && (
				<AdditionalInfoPopup
					isOpen={modalIsOpen}
					setIsOpen={setModalIsOpen}
					product={product}
				/>
			)}
		</div>
	);
}
